import { getStrategyLegSemantics, getUnderlyingBiasFromOpenType, summarizeLegUnderlyingBias } from '@einhaus/project314-shared';
import type {
    EntityOptionMetadata,
    EntityType,
    DerivativeType,
    ExitLogicEntryContext,
    PositionSide,
    TransactionType,
    TradingStrategyLeg,
    UnderlyingBias
} from '@einhaus/project314-types';

export type UnderlyingBiasSummary = UnderlyingBias | 'mixed' | 'unknown';

export const formatPositionSide = (positionSide: PositionSide | 'either' | null | undefined): string => {
    if (positionSide === 'long') return 'Long';
    if (positionSide === 'short') return 'Short';
    if (positionSide === 'either') return 'Either';
    return 'Unknown';
};

export const formatUnderlyingBias = (underlyingBias: UnderlyingBiasSummary | 'both' | null | undefined): string => {
    if (underlyingBias === 'bullish') return 'Bullish';
    if (underlyingBias === 'bearish') return 'Bearish';
    if (underlyingBias === 'mixed') return 'Mixed';
    if (underlyingBias === 'both') return 'Both';
    return 'Unknown';
};

export const formatOpeningAction = (openingAction: TradingStrategyLeg['openTransactionType']): string => {
    if (openingAction === 'buyToOpen') return 'Buy to open';
    if (openingAction === 'sellToOpen') return 'Sell to open';
    if (openingAction === 'shortSell') return 'Short sell';
    return 'Buy';
};

export const formatStrategyLegSemantics = (leg: TradingStrategyLeg): string => {
    const instrument =
        leg.derivativeType === 'Option' ? (leg.optionType ?? 'option') : leg.derivativeType === 'Future' ? 'future' : 'stock/crypto';
    const summary = `${formatPositionSide(leg.positionSide)} ${instrument}`;
    const exposure = leg.underlyingBias ? ` · ${formatUnderlyingBias(leg.underlyingBias)}` : '';

    return `${summary}${exposure} · ${formatOpeningAction(leg.openTransactionType)}`;
};

type TradeSemanticTransaction = {
    entityId: number;
    entityType: EntityType;
    derivativeId?: number | null;
    derivativeType: DerivativeType | null;
    isOpeningTrade: boolean | number;
    type: TransactionType;
};

type TradeWithTransactions = {
    entryContext?: Pick<ExitLogicEntryContext, 'portfolioTradeAdmissionSnapshot'> | null;
    transactions: readonly TradeSemanticTransaction[];
};

const optionTypeForTransaction = (
    transaction: TradeSemanticTransaction,
    options: readonly Pick<EntityOptionMetadata, 'id' | 'type' | 'assetId' | 'entityType'>[]
): 'call' | 'put' | null => {
    if (transaction.derivativeType !== 'Option' || transaction.derivativeId == null) return null;
    const option = options.find(
        (candidate) =>
            candidate.id === transaction.derivativeId &&
            candidate.assetId === transaction.entityId &&
            candidate.entityType === transaction.entityType
    );
    if (!option) return null;
    return option.type === 'c' ? 'call' : 'put';
};

export const getTradeUnderlyingBiasSummary = (
    trade: TradeWithTransactions,
    options: readonly Pick<EntityOptionMetadata, 'id' | 'type' | 'assetId' | 'entityType'>[] = []
): UnderlyingBiasSummary => {
    const reservedPositions = trade.entryContext?.portfolioTradeAdmissionSnapshot?.positions;
    if (reservedPositions?.length) return summarizeLegUnderlyingBias(reservedPositions);

    const openingTransactions = trade.transactions.filter(
        (transaction) => transaction.isOpeningTrade === true || transaction.isOpeningTrade === 1
    );

    return summarizeLegUnderlyingBias(
        openingTransactions.map((transaction) => {
            if (transaction.derivativeType === 'Future') {
                if (transaction.type !== 'buyToOpen' && transaction.type !== 'sellToOpen') return { underlyingBias: null };
                return getStrategyLegSemantics({ derivativeType: 'Future', openTransactionType: transaction.type, optionType: null });
            }
            if (transaction.derivativeType === 'Option') {
                const optionType = optionTypeForTransaction(transaction, options);
                if (optionType === null || (transaction.type !== 'buyToOpen' && transaction.type !== 'sellToOpen')) {
                    return { underlyingBias: null };
                }
                return { underlyingBias: getUnderlyingBiasFromOpenType(transaction.type, optionType) };
            }
            if (transaction.type !== 'buy' && transaction.type !== 'shortSell') return { underlyingBias: null };
            return { underlyingBias: getUnderlyingBiasFromOpenType(transaction.type, null) };
        })
    );
};
